import * as React from 'react';
import { isEmpty, isNil } from 'lodash';
import classnames from 'classnames';

import PageHeading from 'components/page/page-heading/PageHeading';

import CampaignVisibilityChart from '../campaign-visibility-chart/CampaignVisibilityChart';
import CampaignBudgetByTaskWidget from '../campaign-budget-by-task-widget/CampaignBudgetByTaskWidget';
import CreatorTaskProgressWidget from '../creator-task-progress-widget/CreatorTaskProgressWidget';
import ReportingStatisticsTableWithBenchmark from '../reporting-statistics-table-with-benchmark/ReportingStatisticsTableWithBenchmark';
import CampaignIgAudienceData from '../campaign-ig-audience-data/CampaignIgAudienceData';
import CampaignEngagementWidget from '../campaign-engagement-widget/CampaignEngagementWidget';
import ReportingComponentHeader from '../reporting-component-header/ReportingComponentHeader';
import ReportingPosts from '../reporting-posts/ReportingPosts';


import { addUserAction } from 'modules/tracking/providers/user-action-provider';
import { getChangeSortingOfReportingForInstagramReelsEventLabel } from 'modules/tracking/utils/user-actions';
import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { DEFAULT_VALUE, getChartItems, getVisibilityGraphItems, getXAxisSeries } from '../../utils/reporting-task-types';
import { abbrNum, getMetricValueInPercentage } from 'utils/helper/common';
import { DELIVERABLE_TYPE_INSTAGRAM_REEL } from 'utils/deliverables';

import type { Campaign } from 'types';
import type {
  InstagramReelReporting as InstagramReelReportingAlias,
  InstagramReelReportingAudienceComponentData,
  ReportingSummaryInfluencersMap
} from 'modules/reporting/types';
import type { ReportingStatisticTableWithBenchmarkItem } from '../../types/ReportingStatisticTableWithBenchmarkItem.type';

applyTranslations(require('./instagram-reel-reporting.i18n.json'));

import './instagram-reel-reporting.scss';

type OwnProps = {
  campaign: Campaign,
  instagramReelReporting: InstagramReelReportingAlias,
  isLoading: boolean,
  hideTitle?: boolean,
  hideCreators?: boolean,
  hideInfo?: boolean,
  audienceData: InstagramReelReportingAudienceComponentData,
  hasDefinedAttribution: boolean,
  influencersMapById: ReportingSummaryInfluencersMap
};

type Props = OwnProps;

type State = {
  isAwarenessCollapsed: boolean,
  isEngagementCollapsed: boolean
};

class InstagramReelReporting extends React.Component<Props, State> {
  state = {
    isAwarenessCollapsed: false,
    isEngagementCollapsed: false
  };

  getIGStatisticsItems(): Array<ReportingStatisticTableWithBenchmarkItem> {
    const { instagramReelReporting: { visibility }, hasDefinedAttribution } = this.props;
    const {
      costPerMille,
      reachToFollowersRatio,
      reachToFollowersBenchmark,
      reachToFollowersBenchmarkDelta,
      impressionsToFollowersRatio,
      impressionsToFollowersBenchmark,
      impressionsToFollowersDelta
    } = visibility || {};

    return [{
      key: 'cost_per_mille',
      label: __t('instagram_reel_reporting.visibility.cost_per_mille_label'),
      value: renderPrice(costPerMille, { defaultValue: DEFAULT_VALUE }),
      tooltip: __t('instagram_reel_reporting.visibility.tooltips.cost_per_mile_tooltip'),
      hasDefinedAttribution
    },
    {
      key: 'reach_to_follower_ratio',
      label: __t('instagram_reel_reporting.visibility.react_to_follower_ratio_label'),
      value: getMetricValueInPercentage(reachToFollowersRatio),
      tooltip: __t('instagram_reel_reporting.visibility.tooltips.reach_to_follower_ratio_tooltip'),
      benchmarkValue: !isNil(reachToFollowersBenchmark) && getMetricValueInPercentage(reachToFollowersBenchmark),
      deltaValue: reachToFollowersBenchmarkDelta
    },
    {
      key: 'impression_to_follower_ratio',
      label: __t('instagram_reel_reporting.visibility.impression_to_follower_ratio'),
      value: getMetricValueInPercentage(impressionsToFollowersRatio),
      tooltip: __t('instagram_reel_reporting.visibility.tooltips.impression_to_follower_ratio_tooltip'),
      benchmarkValue: !isNil(impressionsToFollowersBenchmark) && getMetricValueInPercentage(impressionsToFollowersBenchmark),
      deltaValue: impressionsToFollowersDelta
    }];
  }

  getIGEngagementItems(): Array<ReportingStatisticTableWithBenchmarkItem> {
    const { instagramReelReporting: { engagement, visibility }, hasDefinedAttribution } = this.props;

    const {
      costPerEngagement,
      engagementRateFollowers,
      engagementRateFollowersBenchmark,
      engagementRateFollowersDelta,
      engagementRateImpressions,
      engagementRateImpressionsBenchmark,
      engagementRateImpressionsDelta
    } = engagement || {};

    const { agreedFollowers, totalImpressions } = visibility || {};

    return [{
      key: 'cost_per_engagement',
      label: __t('instagram_reel_reporting.engagement.cost_per_engagement'),
      value: renderPrice(costPerEngagement, { defaultValue: DEFAULT_VALUE }),
      tooltip: __t('instagram_posts_reporting.engagement.tooltips.cost_per_engagement_tooltip'),
      hasDefinedAttribution
    },
    {
      key: 'engagement_rate_followers',
      label: __t('instagram_reel_reporting.engagement.engagement_rate_followers',
        { agreedFollowers: abbrNum(agreedFollowers, { decimal: 1 }, true) }),
      value: getMetricValueInPercentage(engagementRateFollowers),
      tooltip: __t('instagram_reel_reporting.engagement.tooltips.engagement_rate_followers_tooltip'),
      benchmarkValue: !isNil(engagementRateFollowersBenchmark) && getMetricValueInPercentage(engagementRateFollowersBenchmark),
      deltaValue: engagementRateFollowersDelta
    },
    {
      key: 'engagement_rate_impressions',
      label: __t('instagram_reel_reporting.engagement.engagement_rate_impressions',
        { totalImpressions: abbrNum(totalImpressions, { decimal: 1 }, true) }),
      value: getMetricValueInPercentage(engagementRateImpressions),
      tooltip: __t('instagram_reel_reporting.engagement.tooltips.engagement_rate_impressions_tooltip'),
      benchmarkValue: !isNil(engagementRateImpressionsBenchmark) && getMetricValueInPercentage(engagementRateImpressionsBenchmark),
      deltaValue: engagementRateImpressionsDelta
    }];
  }

  toggleCollapsed(collapseKey: keyof State): void {
    this.setState(prevState => ({
      ...prevState,
      [collapseKey]: !prevState[collapseKey]
    }));
  }

  renderHeader(): React.ReactNode {
    return (
      <div className="instagram-reel-reporting__heading">
        <PageHeading
          heading={__t('instagram_reel_reporting.page_heading')}
          color="light"
          size="h1"
          margin="none"
        />
      </div>
    );
  }

  render() {
    const {
      campaign,
      instagramReelReporting,
      isLoading,
      hideTitle,
      hideInfo,
      hideCreators,
      audienceData,
      hasDefinedAttribution,
      influencersMapById
    } = this.props;

    const { isAwarenessCollapsed, isEngagementCollapsed } = this.state;
    const hasData = !isEmpty(instagramReelReporting);

    const awarenessContainerClasses = classnames('instagram-reel-reporting__collapsible', {
      'instagram-reel-reporting__collapsible--awareness': true,
      'instagram-reel-reporting__collapsible--collapsed': isAwarenessCollapsed
    });
    const engagementContainerClasses = classnames('instagram-reel-reporting__collapsible', {
      'instagram-reel-reporting__collapsible--engagement': true,
      'instagram-reel-reporting__collapsible--collapsed': isEngagementCollapsed
    });

    if (!hasData && !isLoading) {
      return (
        <section className="instagram-reel-reporting__empty-message">
          {__t('instagram_reel_reporting.no_data_error')}
        </section>
      );
    }

    const { recap,
      visibility,
      posts,
      engagement
    } = instagramReelReporting;
    const { totalReactions, saves, comments, likes, shares } = engagement || {};

    return (
      <div className="instagram-reel-reporting">
        {!hideTitle && this.renderHeader()}
        {
          !hideInfo && (
            <div className="instagram-reel-reporting__ig-reporting">
              <CreatorTaskProgressWidget
                creatorBarChartItems={getChartItems(DELIVERABLE_TYPE_INSTAGRAM_REEL, recap, 'creators')}
                taskProgressBarChartItems={getChartItems(DELIVERABLE_TYPE_INSTAGRAM_REEL, recap, 'tasks')}
                hideCreators={hideCreators}
                isLoading={isLoading}
              />
              <CampaignBudgetByTaskWidget
                recap={recap}
                campaignId={campaign.id}
                taskType="instagram_reel"
                hasDefinedAttribution={hasDefinedAttribution}
                isLoading={isLoading}
                view={hideCreators ? 'small' : 'normal'}
              />
            </div>
          )
        }
        <div className="instagram-reel-reporting__awareness">
          <ReportingComponentHeader
            isCollapsed={isAwarenessCollapsed}
            toggleCollapsed={() => this.toggleCollapsed('isAwarenessCollapsed')}
            title={__t('instagram_reel_reporting.awareness_title')}
          />
          <div className={awarenessContainerClasses}>
            <div className="instagram-reel-reporting-visibility">
              <CampaignVisibilityChart
                visibilityGraphItems={getVisibilityGraphItems(visibility, DELIVERABLE_TYPE_INSTAGRAM_REEL)}
                xAxisSeries={getXAxisSeries(visibility)}
                isLoading={isLoading}
              />
              <ReportingStatisticsTableWithBenchmark
                statisticsItems={this.getIGStatisticsItems()}
                campaignId={campaign.id}
                wrapperClass="instagram-reel-reporting__reporting-statistics-table"
                isLoading={isLoading}
              />
            </div>
            <CampaignIgAudienceData
              audienceData={audienceData}
              type="reels"
            />
          </div>
        </div>
        <div className="instagram-reel-reporting__engagement">
          <ReportingComponentHeader
            isCollapsed={isEngagementCollapsed}
            toggleCollapsed={() => this.toggleCollapsed('isEngagementCollapsed')}
            title={__t('instagram_reel_reporting.engagement_title')}
          />
          <div className={engagementContainerClasses}>
            <CampaignEngagementWidget
              engagementTableItems={this.getIGEngagementItems()}
              totalReactions={totalReactions || 0}
              campaignId={campaign.id}
              engagementData={{
                saves: saves || 0,
                comments: comments || 0,
                likes: likes || 0,
                shares: shares || 0
              }}
              isLoading={isLoading}
              taskType="instagram_reel"
            />
          </div>
        </div>
        <div className="instagram-reel-reporting__stories-container">
          <ReportingPosts
            type="instagram_reel"
            posts={posts}
            influencersMapById={influencersMapById}
            isLoading={isLoading}
            onSortingChange={sortingType => (
              addUserAction({
                eventCategory: 'instagram reels reporting',
                eventAction: 'change sorting',
                eventLabel: getChangeSortingOfReportingForInstagramReelsEventLabel(sortingType)
              })
            )}
          />
        </div>
      </div>
    );
  }
}

export default InstagramReelReporting;
